package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** The shelf of poets (`/gems/poetry`). */
data class PoetryUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val poets: List<Poet> = emptyList(),
    /** Published poems per poet id — the count pill on each shelf card. */
    val poemCounts: Map<String, Int> = emptyMap(),
)

/**
 * Streams the published poets in hub (chronological) order, plus a poem count per poet. The
 * counts come from the same `poems` stream the readers use (the corpus is a few dozen docs), so
 * a poet whose poems fail to load simply shows no pill rather than blocking the shelf.
 */
class PoetryViewModel(
    gemsRepository: GemsRepository,
) : ViewModel() {

    val state: StateFlow<PoetryUiState> =
        combine(gemsRepository.observePoets(), gemsRepository.observePoems()) { poetsResult, poemsResult ->
            val counts = (poemsResult as? DataResult.Success)?.data.orEmpty()
                .groupingBy { it.poetId }.eachCount()
            when (poetsResult) {
                is DataResult.Success -> PoetryUiState(loading = false, poets = poetsResult.data, poemCounts = counts)
                is DataResult.Failure -> PoetryUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), PoetryUiState())
}

/** One poet and their poems (`/gems/poetry/{poetId}`). */
data class PoetPoemsUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val poet: Poet? = null,
    val poems: List<Poem> = emptyList(),
)

/**
 * Loads one poet's poems. The poet itself comes from the same `poets` stream the shelf uses rather
 * than a second document read — the corpus is small and already in memory.
 */
class PoetPoemsViewModel(
    gemsRepository: GemsRepository,
    private val poetId: String,
) : ViewModel() {

    val state: StateFlow<PoetPoemsUiState> =
        combine(
            gemsRepository.observePoets(),
            gemsRepository.observePoems(poetId),
        ) { poetsResult, poemsResult ->
            val poet = (poetsResult as? DataResult.Success)?.data?.firstOrNull { it.id == poetId }
            when (poemsResult) {
                is DataResult.Success -> PoetPoemsUiState(loading = false, poet = poet, poems = poemsResult.data)
                is DataResult.Failure -> PoetPoemsUiState(loading = false, error = true, poet = poet)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), PoetPoemsUiState())
}

/** One poem, opened from a poet's list or a deep link, plus its like/save/report state. */
data class PoemDetailUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val poem: Poem? = null,
    val poet: Poet? = null,
    val liked: Boolean = false,
    val saved: Boolean = false,
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/** Loads a single poem (deep-link safe) and drives the same interactions as a tale. */
class PoemDetailViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
    private val poemId: String,
) : ViewModel() {

    private val loaded = MutableStateFlow<DataResult<Poem>?>(null)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    init {
        viewModelScope.launch { loaded.value = gemsRepository.getPoem(poemId) }
    }

    // Two nested combines: the typed overloads stop at five sources and this needs six.
    private val content = combine(loaded, gemsRepository.observePoets()) { result, poetsResult ->
        result to (poetsResult as? DataResult.Success)?.data.orEmpty()
    }

    val state: StateFlow<PoemDetailUiState> =
        combine(
            content,
            gemsRepository.observeLikedPoemIds(),
            savedController.saved,
            signInGate,
            reportStatus,
        ) { (result, poets), liked, saved, signIn, report ->
            when (result) {
                null -> PoemDetailUiState(loading = true)
                is DataResult.Success -> PoemDetailUiState(
                    loading = false,
                    poem = result.data,
                    poet = poets.firstOrNull { it.id == result.data.poetId },
                    liked = poemId in liked,
                    saved = saved.any { it.type == GemType.Poem && it.id == poemId },
                    signInRequired = signIn,
                    reportStatus = report,
                )
                is DataResult.Failure -> PoemDetailUiState(loading = false, error = true, signInRequired = signIn, reportStatus = report)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), PoemDetailUiState())

    fun toggleLike() {
        val liked = state.value.liked
        viewModelScope.launch {
            val result = gemsRepository.setPoemLiked(poemId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) signInGate.value = true
        }
    }

    fun toggleSave() {
        viewModelScope.launch { savedController.toggle(SavedGem(id = poemId, type = GemType.Poem)) }
    }

    fun consumeSignInGate() { signInGate.value = false }

    fun report(reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val r = reportRepository.fileReport("poem", poemId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (r.error is AppError.Unauthorized) GemReportStatus.SignInRequired else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() { reportStatus.value = GemReportStatus.Idle }
}
